import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "motion/react";
import { Link, useNavigate } from "react-router-dom";
import {
  X,
  UserPlus,
  Users,
  Bell,
  BellRing,
  FileText,
  Heart,
  MessageCircle,
  Reply,
  AtSign,
  Settings,
  CheckCheck,
  Trash2,
  Loader2,
} from "lucide-react";

import { Button } from "../ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "../ui/avatar";
import { Skeleton } from "../ui/skeleton";
import ApiService from "../../services/api-service";
import { relativeTime } from "../../lib/format";
import { NOTIFICATIONS_CHANGED_EVENT } from "../../lib/push";
import { PushOptInCard } from "./PushOptInCard";
import type { AppNotification, NotificationType, SuggestedUser } from "../../types/social";

interface NotificationCenterProps {
  open: boolean;
  onClose: () => void;
}

type Filter = "all" | "unread" | "read";

const PAGE_SIZE = 20;

const FILTERS: { value: Filter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "unread", label: "Unread" },
  { value: "read", label: "Read" },
];

// Small badge on the avatar saying what kind of notification it is.
const TYPE_ICONS: Record<NotificationType, { Icon: typeof Bell; className: string; label: string }> = {
  follow: { Icon: UserPlus, className: "bg-sky-500", label: "New follower" },
  new_content: { Icon: FileText, className: "bg-amber-500", label: "New post" },
  like: { Icon: Heart, className: "bg-rose-500", label: "Like" },
  comment: { Icon: MessageCircle, className: "bg-emerald-500", label: "Comment" },
  reply: { Icon: Reply, className: "bg-violet-500", label: "Reply" },
  mention: { Icon: AtSign, className: "bg-indigo-500", label: "Mention" },
};

// Wording for rows saved before notifications stored their own text.
const FALLBACK_TEXT: Record<NotificationType, string> = {
  follow: "started following you.",
  new_content: "shared something new.",
  like: "liked your post.",
  comment: "commented on your post.",
  reply: "replied to your comment.",
  mention: "mentioned you.",
};

const EMPTY_TEXT: Record<Filter, string> = {
  all: "No notifications yet. When people follow you, or someone you follow posts, it shows up here.",
  unread: "You're all caught up.",
  read: "Nothing read yet.",
};

const notifyChanged = () => window.dispatchEvent(new Event(NOTIFICATIONS_CHANGED_EVENT));

function initials(name: string) {
  return name.slice(0, 2).toUpperCase() || "?";
}

function RowSkeleton() {
  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <Skeleton className="h-11 w-11 rounded-full shrink-0" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-3.5 w-48" />
        <Skeleton className="h-3 w-20" />
      </div>
    </div>
  );
}

// The actor's name in bold, then the rest of the notification text.
function NotificationText({ item }: { item: AppNotification }) {
  const name = item.actor_first_name || item.actor_username;
  const message = item.message || `${name} ${FALLBACK_TEXT[item.type]}`;
  if (message.startsWith(name)) {
    return (
      <>
        <span className="font-semibold">{name}</span>
        {message.slice(name.length)}
      </>
    );
  }
  return <>{message}</>;
}

// Notification centre (the bell): every notification with All / Unread / Read
// tabs, mark-as-read, delete and infinite scroll, plus people to follow.
export function NotificationCenter({ open, onClose }: NotificationCenterProps) {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<Filter>("all");
  const [items, setItems] = useState<AppNotification[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [nextOffset, setNextOffset] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [retryToken, setRetryToken] = useState(0);
  const [suggested, setSuggested] = useState<SuggestedUser[]>([]);
  const [busyId, setBusyId] = useState<number | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  // Ignores responses for a tab the user has already left.
  const requestRef = useRef(0);

  // First page whenever the panel opens or the tab changes.
  useEffect(() => {
    if (!open) return;
    const requestId = ++requestRef.current;
    setIsLoading(true);
    setHasError(false);
    ApiService.getNotifications({ filter, offset: 0, limit: PAGE_SIZE })
      .then((response: { notifications?: AppNotification[]; hasMore?: boolean; nextOffset?: number }) => {
        if (requestId !== requestRef.current) return;
        setItems(response?.notifications || []);
        setHasMore(Boolean(response?.hasMore));
        setNextOffset(response?.nextOffset || 0);
      })
      .catch(() => {
        if (requestId === requestRef.current) setHasError(true);
      })
      .finally(() => {
        if (requestId === requestRef.current) setIsLoading(false);
      });
  }, [open, filter, retryToken]);

  // People to follow, below the list on the All tab.
  useEffect(() => {
    if (!open) return;
    ApiService.getFriendRecommendations()
      .then((response: { suggested?: SuggestedUser[] }) => setSuggested(response?.suggested || []))
      .catch(() => setSuggested([]));
  }, [open]);

  const loadMore = useCallback(() => {
    if (isLoading || isLoadingMore || !hasMore) return;
    const requestId = requestRef.current;
    setIsLoadingMore(true);
    ApiService.getNotifications({ filter, offset: nextOffset, limit: PAGE_SIZE })
      .then((response: { notifications?: AppNotification[]; hasMore?: boolean; nextOffset?: number }) => {
        if (requestId !== requestRef.current) return;
        // New notifications can shift the offsets; skip any already shown.
        setItems((prev) => {
          const seen = new Set(prev.map((item) => item.id));
          return [...prev, ...(response?.notifications || []).filter((item) => !seen.has(item.id))];
        });
        setHasMore(Boolean(response?.hasMore));
        setNextOffset(response?.nextOffset || nextOffset);
      })
      .catch(() => setHasMore(false))
      .finally(() => setIsLoadingMore(false));
  }, [filter, hasMore, isLoading, isLoadingMore, nextOffset]);

  // Infinite scroll: load the next page when the end of the list comes into view.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!open || !sentinel || !hasMore) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) loadMore();
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [open, hasMore, loadMore, items.length]);

  const markRead = (item: AppNotification) => {
    if (item.is_read) return;
    setItems((prev) =>
      filter === "unread"
        ? prev.filter((entry) => entry.id !== item.id)
        : prev.map((entry) => (entry.id === item.id ? { ...entry, is_read: 1 } : entry))
    );
    ApiService.markNotificationRead(item.id).then(notifyChanged).catch(() => {});
  };

  const handleOpen = (item: AppNotification) => {
    markRead(item);
    onClose();
    navigate(item.deep_link);
  };

  const handleDelete = async (item: AppNotification) => {
    setBusyId(item.id);
    try {
      await ApiService.deleteNotification(item.id);
      setItems((prev) => prev.filter((entry) => entry.id !== item.id));
      notifyChanged();
    } catch {
      // Leave it in the list.
    } finally {
      setBusyId(null);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await ApiService.markNotificationsRead();
      setItems((prev) => (filter === "unread" ? [] : prev.map((entry) => ({ ...entry, is_read: 1 }))));
      notifyChanged();
    } catch {
      // Nothing changes; the user can try again.
    }
  };

  const handleFollowToggle = async (userId: number) => {
    setBusyId(-userId);
    try {
      const response = await ApiService.toggleFollow(userId);
      const isFollowing = Boolean(response?.following);
      setItems((prev) =>
        prev.map((item) => (item.actor_id === userId ? { ...item, is_following_back: isFollowing ? 1 : 0 } : item))
      );
      setSuggested((prev) => (isFollowing ? prev.filter((user) => user.id !== userId) : prev));
    } catch {
      // Leave state untouched; the button keeps its label.
    } finally {
      setBusyId(null);
    }
  };

  const hasUnread = items.some((item) => !item.is_read);

  // Portalled to <body> so it sits above the app bar (also portalled there) on
  // every page, whatever stacking context the page creates.
  const panel = (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="notification-center-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-50 bg-black/50"
            onClick={onClose}
            aria-hidden="true"
          />

          <motion.aside
            key="notification-center"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
            role="dialog"
            aria-modal="true"
            aria-label="Notifications"
            className="fixed inset-y-0 right-0 z-50 flex h-full w-full max-w-md flex-col border-l border-slate-200 bg-white pt-[var(--safe-area-inset-top,env(safe-area-inset-top))] pb-[var(--safe-area-inset-bottom,env(safe-area-inset-bottom))] shadow-2xl dark:border-slate-700 dark:bg-slate-900"
          >
            <div className="border-b border-slate-200 px-4 pb-3 pt-4 dark:border-slate-700">
              <div className="flex items-center justify-between gap-2">
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Notifications</h2>
                <div className="flex items-center gap-1">
                  <Button asChild variant="ghost" size="icon" className="h-10 w-10" aria-label="Notification settings">
                    <Link to="/settings/notifications" onClick={onClose}>
                      <Settings className="h-5 w-5" />
                    </Link>
                  </Button>
                  <Button variant="ghost" size="icon" className="h-10 w-10" onClick={onClose} aria-label="Close">
                    <X className="h-5 w-5" />
                  </Button>
                </div>
              </div>

              <div className="mt-3 flex items-center justify-between gap-2">
                <div className="flex gap-1 rounded-full bg-slate-100 p-1 dark:bg-slate-800" role="tablist">
                  {FILTERS.map(({ value, label }) => (
                    <button
                      key={value}
                      type="button"
                      role="tab"
                      aria-selected={filter === value}
                      onClick={() => setFilter(value)}
                      className={`rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${
                        filter === value
                          ? "bg-white text-slate-900 shadow-sm dark:bg-slate-700 dark:text-white"
                          : "text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                {hasUnread && filter !== "read" && (
                  <Button variant="ghost" size="sm" onClick={handleMarkAllRead} className="h-8 rounded-full px-3 text-xs">
                    <CheckCheck className="mr-1 h-3.5 w-3.5" />
                    Mark all as read
                  </Button>
                )}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto themed-scrollbar">
              <PushOptInCard variant="compact" />

              {isLoading ? (
                <div className="divide-y divide-slate-100 dark:divide-slate-800">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <RowSkeleton key={index} />
                  ))}
                </div>
              ) : hasError ? (
                <div className="flex flex-col items-center gap-3 p-8 text-center text-sm text-slate-500 dark:text-slate-400">
                  Couldn't load notifications.
                  <Button variant="outline" size="sm" onClick={() => setRetryToken((n) => n + 1)}>
                    Try again
                  </Button>
                </div>
              ) : items.length === 0 ? (
                <div className="flex flex-col items-center gap-3 px-8 py-12 text-center text-sm text-slate-500 dark:text-slate-400">
                  <span className="grid h-14 w-14 place-items-center rounded-full bg-slate-100 dark:bg-slate-800">
                    <BellRing className="h-6 w-6 text-slate-400 dark:text-slate-500" />
                  </span>
                  {EMPTY_TEXT[filter]}
                </div>
              ) : (
                <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                  {items.map((item) => {
                    const name = item.actor_first_name || item.actor_username;
                    const { Icon, className: iconClass, label } = TYPE_ICONS[item.type] || TYPE_ICONS.follow;
                    const unread = !item.is_read;
                    return (
                      <li
                        key={item.id}
                        className={`group relative flex items-start gap-3 px-4 py-3 transition-colors ${
                          unread ? "bg-amber-50/60 dark:bg-slate-800/60" : ""
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => handleOpen(item)}
                          className="flex min-w-0 flex-1 items-start gap-3 text-left"
                        >
                          <span className="relative shrink-0">
                            <Avatar className="h-11 w-11">
                              {item.actor_profile_pic ? (
                                <AvatarImage src={item.actor_profile_pic} alt="" className="object-cover" />
                              ) : null}
                              <AvatarFallback className="bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                                {initials(name)}
                              </AvatarFallback>
                            </Avatar>
                            <span
                              className={`absolute -bottom-0.5 -right-0.5 grid h-5 w-5 place-items-center rounded-full text-white ring-2 ring-white dark:ring-slate-900 ${iconClass}`}
                              title={label}
                            >
                              <Icon className="h-3 w-3" aria-hidden="true" />
                            </span>
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="line-clamp-3 text-sm text-slate-800 dark:text-slate-100">
                              <NotificationText item={item} />
                            </span>
                            <span className="mt-0.5 block text-xs text-slate-500 dark:text-slate-400">
                              {relativeTime(item.created_at)}
                            </span>
                          </span>
                        </button>

                        <div className="flex shrink-0 items-center gap-1">
                          {item.type === "follow" && (
                            <Button
                              size="sm"
                              variant={item.is_following_back ? "outline" : "default"}
                              disabled={busyId === -item.actor_id}
                              onClick={() => handleFollowToggle(item.actor_id)}
                              className="h-8 rounded-full px-3 text-xs"
                            >
                              {item.is_following_back ? "Following" : "Follow back"}
                            </Button>
                          )}
                          {unread && <span className="h-2 w-2 rounded-full bg-amber-500" aria-label="Unread" />}
                          <button
                            type="button"
                            onClick={() => handleDelete(item)}
                            disabled={busyId === item.id}
                            aria-label="Delete notification"
                            className="grid h-9 w-9 place-items-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-red-600 dark:hover:bg-slate-800"
                          >
                            {busyId === item.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}

              {hasMore && !isLoading && (
                <div ref={sentinelRef} className="flex justify-center py-4">
                  {isLoadingMore && <Loader2 className="h-5 w-5 animate-spin text-slate-400" aria-label="Loading more" />}
                </div>
              )}

              {filter === "all" && !isLoading && !hasMore && suggested.length > 0 && (
                <section className="border-t border-slate-100 dark:border-slate-800">
                  <h3 className="flex items-center gap-1.5 px-4 pt-4 pb-1 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    <Users className="h-3.5 w-3.5" /> Suggested for you
                  </h3>
                  <div className="divide-y divide-slate-100 dark:divide-slate-800">
                    {suggested.map((user) => {
                      const name = user.first_name || user.username;
                      return (
                        <div key={user.id} className="flex items-center gap-3 px-4 py-3">
                          <Link to={`/user/${user.username}/${user.id}`} onClick={onClose} className="shrink-0">
                            <Avatar className="h-10 w-10">
                              {user.profile_pic ? <AvatarImage src={user.profile_pic} alt="" className="object-cover" /> : null}
                              <AvatarFallback className="bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                                {initials(name)}
                              </AvatarFallback>
                            </Avatar>
                          </Link>
                          <div className="min-w-0 flex-1">
                            <Link
                              to={`/user/${user.username}/${user.id}`}
                              onClick={onClose}
                              className="block truncate text-sm font-medium text-slate-900 hover:underline dark:text-slate-100"
                            >
                              {name}
                            </Link>
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                              {user.shared_interests > 0
                                ? `${user.shared_interests} shared ${user.shared_interests === 1 ? "interest" : "interests"}`
                                : `${user.follower_count} ${user.follower_count === 1 ? "follower" : "followers"}`}
                            </p>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={busyId === -user.id}
                            onClick={() => handleFollowToggle(user.id)}
                            className="shrink-0 rounded-full"
                          >
                            <UserPlus className="mr-1 h-3.5 w-3.5" />
                            Follow
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
  return typeof document !== "undefined" ? createPortal(panel, document.body) : panel;
}
